import { Slide1Template } from '@/templates/Slide1Template';
import { Slide2Template } from '@/templates/Slide2Template';
import { Slide3Template } from '@/templates/Slide3Template';
import { Slide4Template } from '@/templates/Slide4Template';
import { Slide5Template } from '@/templates/Slide5Template';
import { Slide6Template } from '@/templates/Slide6Template';
import { Slide7Template } from '@/templates/Slide7Template';
import { type UserData } from '@/types/userData';
import { type TemplateProps } from '@/utils/types';

export interface TemplateConfig {
  name: string;
  component: React.ComponentType<TemplateProps>;
  getProps: (userData: UserData) => TemplateProps;
  shouldInclude?: (userData: UserData) => boolean;
}

export const calculateLostDaysText = (totalLostDays: number): string => {
  let text = `${totalLostDays} days`;
  switch (totalLostDays) {
    case 12:
      text = 'almost two weeks';
      break;
    case 24:
      text = 'almost a month';
      break;
    case 49:
      text = 'over a month and a half';
      break;
    case 73:
      text = 'more than two months';
      break;
    case 97:
      text = 'over three months';
      break;
  }

  return `That's  ${text}`;
};

export const TEMPLATE_CONFIGS: TemplateConfig[] = [
  {
    name: 'Slide1Template',
    component: Slide1Template,
    getProps: (userData: UserData) => ({
      imageUrl: userData.photoDataUrl,
      text1: userData.ocdYear.toString(),
    }),
  },
  {
    name: 'Slide2Template',
    component: Slide2Template,
    getProps: (userData: UserData) => ({
      text1: `${userData.totalLostDays} days`,
      text2: calculateLostDaysText(userData.totalLostDays),
    }),
  },
  {
    name: 'Slide3Template',
    component: Slide3Template,
    getProps: (userData: UserData) => ({
      text1: userData.selectedAnalogy?.text1 || '💼',
      text2: userData.selectedAnalogy?.text2 || "That's a lot of time lost to OCD",
    }),
    shouldInclude: (userData: UserData) => userData.selectedAnalogy !== null,
  },
  {
    name: 'Slide4Template',
    component: Slide4Template,
    getProps: (userData: UserData) => ({
      text1: userData.selectedOcdFact?.text1 || 'And I am not alone, around',
      text2: userData.selectedOcdFact?.text2 || '240M',
      text3: userData.selectedOcdFact?.text3 || 'People will develop OCD at some point in their life',
    }),
    shouldInclude: (userData: UserData) => userData.selectedOcdFact !== null,
  },
  {
    name: 'Slide5Template',
    component: Slide5Template,
    getProps: (userData: UserData) => ({
      text1: userData.userMessage || "OCD isn't who you are, it's what you've been dealing with ✨",
    }),
    shouldInclude: (userData: UserData) => userData.userMessage.trim().length > 0,
  },
  {
    name: 'Slide6Template',
    component: Slide6Template,
    getProps: () => ({}), // Static template with no dynamic content
  },
  {
    name: 'Slide7Template',
    component: Slide7Template,
    getProps: () => ({}), // Static template with no dynamic content
  },
];

// Helper function to get filtered template configs based on user data
export const getFilteredTemplateConfigs = (userData: UserData): TemplateConfig[] => {
  return TEMPLATE_CONFIGS.filter((config) => !config.shouldInclude || config.shouldInclude(userData));
};
